/**
 * Sprint V101 (Fr 27.02.2026) · Video-Drip-In Dashboard-Kachel.
 *
 * Zeigt eine Orange-Box mit dem HEUTE freigeschalteten Video an. Nur sichtbar
 * für Free-User im 4-Tage-Drip-Fenster (Tag 0..3). Nach Tag 3 verschwindet
 * die Kachel automatisch (`active:false`).
 *
 * Datensparsamkeit: Kein Autoplay, kein externes Netzwerk vor Klick. Nur
 * ein einfacher Fetch auf /api/video-drip/current-tile.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PlayCircle, Sparkles, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { isFree } from "@/lib/licensing";

export default function VideoDripTile({ user }) {
  const [tile, setTile] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isFree(user)) return;
    let cancelled = false;
    api
      .get("/video-drip/current-tile")
      .then((r) => {
        if (!cancelled) setTile(r.data);
      })
      .catch(() => {
        if (!cancelled) setTile({ active: false });
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (!isFree(user)) return null;
  if (!tile || !tile.active) return null;

  const dayLabel = `Tag ${tile.drip_day + 1} von ${tile.total_days}`;
  const headline = tile.is_own_type
    ? `Heute freigeschaltet: dein Fokustyp ${tile.video_title}`
    : `Heute neu: ${tile.video_title}`;
  const climaxTag = tile.is_climax
    ? "Dein Gegenspieler · Climax"
    : null;

  return (
    <button
      type="button"
      onClick={() => navigate(tile.cta_path)}
      data-testid="video-drip-tile"
      data-drip-day={tile.drip_day}
      data-video-key={tile.video_key}
      className="group block w-full text-left rounded-2xl border-2 border-[#F3651D]/50 bg-gradient-to-r from-[#F3651D]/12 via-orange-50 to-white dark:from-[#F3651D]/25 dark:via-slate-800 dark:to-slate-900 hover:border-[#F3651D] hover:shadow-lg transition-all p-5 sm:p-7 relative overflow-hidden"
    >
      <span
        aria-hidden="true"
        className="hidden sm:block absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#F3651D]/20 blur-3xl group-hover:bg-[#F3651D]/30 transition-colors"
      />
      <div className="relative flex items-center gap-4">
        <div className="flex-shrink-0 w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-[#F3651D] text-white flex items-center justify-center shadow-md sm:ring-4 sm:ring-[#F3651D]/20">
          <PlayCircle className="w-6 h-6 sm:w-7 sm:h-7" strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <p
            className="text-[11px] sm:text-xs font-bold uppercase tracking-widest text-[#F3651D] leading-tight flex items-center gap-2"
            data-testid="video-drip-tile-day-label"
          >
            {dayLabel}
            {climaxTag && (
              <span
                data-testid="video-drip-tile-climax-badge"
                className="inline-flex items-center gap-1 rounded-full bg-[#F3651D] text-white text-[9px] sm:text-[10px] px-2 py-0.5 normal-case tracking-wide"
              >
                <Sparkles className="w-3 h-3" /> {climaxTag}
              </span>
            )}
          </p>
          <p
            className="mt-1 text-lg sm:text-xl font-extrabold text-slate-900 dark:text-white leading-tight"
            data-testid="video-drip-tile-headline"
          >
            {headline}
          </p>
          {tile.video_description && (
            <p
              className="mt-1 text-xs sm:text-sm text-slate-600 dark:text-slate-300 line-clamp-2"
              data-testid="video-drip-tile-description"
            >
              {tile.video_description}
            </p>
          )}
        </div>
        <div className="flex-shrink-0 hidden sm:inline-flex items-center gap-2 rounded-xl bg-[#F3651D] hover:bg-[#d95415] text-white px-5 py-3 text-sm font-bold shadow-md group-hover:shadow-lg transition-shadow whitespace-nowrap">
          Jetzt ansehen
          <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
        </div>
        <div className="flex-shrink-0 sm:hidden inline-flex items-center rounded-lg bg-[#F3651D] text-white px-3 py-2 text-xs font-bold whitespace-nowrap">
          <ArrowRight className="w-4 h-4" />
        </div>
      </div>
    </button>
  );
}
